import {
  Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalBody, ModalFooter, ModalCloseButton,
  Button, Text, Box, Divider, Link, VStack,
} from '@chakra-ui/react';

export default function PrivacyDialog({ isOpen, onClose }) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered scrollBehavior="inside">
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent borderRadius="20px" maxH="82vh" mx={{ base: 3, md: "auto" }}>

        <ModalHeader pb={3} pt={6} px={7} borderBottom="1px solid #F2F2F2">
          <Text fontSize="20px" fontWeight="800" color="#1A1A1A" letterSpacing="-0.3px">
            Privacy Policy
          </Text>
          <Text fontSize="12px" color="#9E9E9E" fontWeight="normal" mt="4px">
            Effective Date: April 3, 2026 · Walnut TMA LLC · Dubai, UAE
          </Text>
        </ModalHeader>
        <ModalCloseButton top={5} right={5} />

        <ModalBody py={6} px={7}>
          <VStack align="stretch" spacing={5}>

            <InfoBanner>
              This policy explains how Alpherra collects, uses, stores, and shares your information.
              We are committed to transparent data practices.{" "}
              <Link href="mailto:contact@alpherra.com" color="#CF008A">contact@alpherra.com</Link>
            </InfoBanner>

            <Clause number="1" title="Data Controller">
              Walnut TMA LLC · Dubai, United Arab Emirates ·{" "}
              <Link href="mailto:contact@alpherra.com" color="#CF008A">contact@alpherra.com</Link>
            </Clause>

            <Clause number="2" title="Information We Collect">
              <SectionLabel>(a) Account & Identity</SectionLabel>
              <BulletList items={[
                'Name, email address, job title, and company name provided at registration.',
                'Billing information (processed securely; Alpherra does not store full card details).',
              ]} />
              <SectionLabel mt={3}>(b) Usage & Technical Data</SectionLabel>
              <BulletList items={[
                'Log data: IP address, browser type, device identifiers, and session info.',
                'Feature usage patterns, query frequency, and Platform interaction data.',
              ]} />
              <SectionLabel mt={3}>(c) User-Submitted Project Data</SectionLabel>
              <BulletList items={[
                'Project specs, engineering parameters, material requirements, and quantities.',
                'Uploaded files, drawings, or documents submitted for analysis.',
                'Queries submitted to the AI engine and corresponding outputs generated.',
              ]} />
            </Clause>

            <Clause number="3" title="How We Use Your Information">
              <BulletList items={[
                'To provide, operate, and improve the Platform and its AI-powered features.',
                'To process payments and manage your subscription.',
                'To respond to support requests and communicate about your account.',
                'To detect, prevent, and address fraud, abuse, or security incidents.',
                'To comply with legal obligations applicable in the UAE.',
                'To train, fine-tune, and improve AI models — see Section 4.',
              ]} />
            </Clause>

            <Clause number="4" title="Use of Data for AI Model Training">
              <WarningBox>
                Alpherra may use your project inputs, queries, and AI outputs to train and improve its machine learning models.
                Reasonable measures are applied to pseudonymise identifying information before use in training datasets.
              </WarningBox>
              <BulletList items={[
                'You grant Alpherra a non-exclusive, royalty-free licence for model training purposes.',
                'Enterprise customers may request contractual restrictions in writing.',
                'Teams Plan users may opt out by contacting contact@alpherra.com.',
                'Training data is retained only as long as necessary, then deleted or anonymised.',
              ]} />
            </Clause>

            <Clause number="5" title="Legal Basis for Processing">
              <BulletList items={[
                'Contract performance — to provide subscribed services.',
                'Legitimate interests — improving AI, preventing fraud, ensuring security.',
                'Legal obligation — complying with UAE Federal Decree-Law No. 45 of 2021.',
                'Consent — where explicitly requested (e.g., marketing communications).',
              ]} />
            </Clause>

            <Clause number="6" title="Data Sharing">
              We do not sell your personal data. We may share with service providers, professional advisers,
              law enforcement (when legally required), or successor entities in a merger or acquisition —
              always with appropriate contractual safeguards.
            </Clause>

            <Clause number="7" title="International Data Transfers">
              Data may be processed on servers outside the UAE. Appropriate safeguards are applied consistent
              with UAE data protection requirements.
            </Clause>

            <Clause number="8" title="Data Retention">
              <BulletList items={[
                'Account data: retained for subscription duration + up to 3 years after termination.',
                'Training data: retained in anonymised form as necessary for model integrity.',
                'You may request deletion at any time (see Section 10).',
              ]} />
            </Clause>

            <Clause number="9" title="Security">
              We implement encrypted data transmission (TLS), access controls, and regular security reviews.
              We will notify you promptly in the event of a data breach affecting your rights.
              No method of internet transmission is 100% secure.
            </Clause>

            <Clause number="10" title="Your Rights">
              <BulletList items={[
                'Access — request a copy of your personal data.',
                'Rectification — request correction of inaccurate or incomplete data.',
                'Erasure — request deletion, subject to legal retention obligations.',
                'Restriction — limit processing in certain circumstances.',
                'Portability — receive data in a structured, machine-readable format.',
                'Objection — object to processing based on legitimate interests.',
                'Withdraw consent — at any time without affecting prior processing.',
              ]} />
              <Text fontSize="13px" color="#4F4F4F" lineHeight="1.7" mt={2}>
                Contact <Link href="mailto:contact@alpherra.com" color="#CF008A">contact@alpherra.com</Link> — we respond within 30 days.
              </Text>
            </Clause>

            <Clause number="11" title="Cookies & Tracking">
              The Platform uses cookies for authentication, session management, and analytics only.
              We do not use third-party advertising cookies.
            </Clause>

            <Clause number="12" title="Children's Privacy">
              The Platform is not directed at individuals under 18. Contact us to remove any minor's data promptly.
            </Clause>

            <Clause number="13" title="Changes to This Policy">
              Material changes will be notified via email or in-platform notification before taking effect.
              The Effective Date at the top indicates the current version.
            </Clause>

            <Clause number="14" title="Contact Us">
              <Box
                bg="#FAFAFA"
                border="1px solid #ECECEC"
                borderRadius="12px"
                px={4}
                py={3}
                fontSize="13px"
                color="#4F4F4F"
                lineHeight="1.9"
              >
                <Text fontWeight="700" color="#1A1A1A">Alpherra Privacy Team</Text>
                <Text>Walnut TMA LLC · Dubai, United Arab Emirates</Text>
                <Text>Email: <Link href="mailto:contact@alpherra.com" color="#CF008A">contact@alpherra.com</Link></Text>
                <Text>Website: <Link href="https://www.alpherra.com" color="#CF008A" isExternal>www.alpherra.com</Link></Text>
              </Box>
            </Clause>

            <Divider />
            <Text fontSize="12px" color="#ABABAB" textAlign="center">
              Last updated April 3, 2026 · © 2026 Walnut TMA LLC. All rights reserved.
            </Text>

          </VStack>
        </ModalBody>

        <ModalFooter borderTop="1px solid #F2F2F2" px={7} pb={5}>
          <Button bg="#CF008A" color="white" borderRadius="10px" _hover={{ bg: '#D933A1' }} onClick={onClose} w="100%">
            I understand
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// ── Helpers ──────────────────────────────────────────────────

function Clause({ number, title, children }) {
  return (
    <Box borderBottom="1px solid #F5F5F5" pb={4}>
      <Text fontSize="13px" fontWeight="700" color="#1A1A1A" mb={2}>
        {number}. {title}
      </Text>
      <Box>
        {typeof children === 'string' ? (
          <Text fontSize="13px" color="#6D6D6D" lineHeight="1.8">{children}</Text>
        ) : children}
      </Box>
    </Box>
  );
}

function BulletList({ items }) {
  return (
    <VStack align="stretch" spacing="5px">
      {items.map((item, i) => (
        <Text key={i} fontSize="13px" color="#6D6D6D" lineHeight="1.8" pl={3}>
          — {item}
        </Text>
      ))}
    </VStack>
  );
}

function InfoBanner({ children }) {
  return (
    <Box borderLeft="3px solid #CF008A" pl={4} py={1}>
      <Text fontSize="13px" color="#6D6D6D" lineHeight="1.8">{children}</Text>
    </Box>
  );
}

function WarningBox({ children }) {
  return (
    <Box bg="#FAFAFA" borderRadius="10px" px={4} py={3} mb={3}>
      <Text fontSize="13px" color="#4F4F4F" fontWeight="500" lineHeight="1.8">{children}</Text>
    </Box>
  );
}

function SectionLabel({ children, ...props }) {
  return (
    <Text fontSize="12px" fontWeight="600" color="#9E9E9E" textTransform="uppercase"
      letterSpacing="0.6px" mb={2} {...props}>
      {children}
    </Text>
  );
}